import { useState, useEffect } from 'react';

export interface BrowserDetectionResult {
  browser: 'chrome' | 'firefox' | 'safari' | 'edge' | 'brave' | 'other';
  browserVersion: string;
  isChromiumBased: boolean;
  isUnsupportedBrowser: boolean;
  unsupportedBrowserType: 'firefox' | 'safari' | 'other' | null;
  canRecord: boolean;
  warningMessage?: string;
}

function getBrowserVersion(userAgent: string): string {
  const matches = userAgent.match(/(?:chrome|firefox|safari|edg|brave)\/([0-9.]+)/i);
  return matches ? matches[1].split('.')[0] : 'unknown';
}

function getWarningMessage(browser: BrowserDetectionResult['browser']): string | undefined {
  const messages: Record<string, string> = {
    firefox: 'Firefox is not supported. Please use Chrome, Edge, or Brave for optimal recording quality.',
    safari: 'Safari has limited codec support. For best results, use Chrome, Edge, or Brave.',
    other: 'Your browser may not support all recording features. Please use Chrome, Edge, or Brave.'
  };
  
  return messages[browser];
}

export function useBrowserDetection(): BrowserDetectionResult {
  const [detection, setDetection] = useState<BrowserDetectionResult | null>(null);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detect browser
    let browser: BrowserDetectionResult['browser'] = 'other';
    let isChromiumBased = false;
    
    if (userAgent.includes('firefox')) {
      browser = 'firefox';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      browser = 'safari';
    } else if (userAgent.includes('edg/')) {
      browser = 'edge';
      isChromiumBased = true;
    } else if (userAgent.includes('brave')) {
      browser = 'brave';
      isChromiumBased = true;
    } else if (userAgent.includes('chrome')) {
      browser = 'chrome';
      isChromiumBased = true;
    }

    // Determine if unsupported
    const isUnsupportedBrowser = browser === 'firefox' || browser === 'safari';
    const unsupportedBrowserType: BrowserDetectionResult['unsupportedBrowserType'] = 
      isUnsupportedBrowser ? (browser as 'firefox' | 'safari') : null;
    const canRecord = isChromiumBased;

    const result: BrowserDetectionResult = {
      browser,
      browserVersion: getBrowserVersion(userAgent),
      isChromiumBased,
      isUnsupportedBrowser,
      unsupportedBrowserType,
      canRecord,
      warningMessage: getWarningMessage(browser)
    };

    setDetection(result);
  }, []);

  return detection || {
    browser: 'other',
    browserVersion: 'unknown',
    isChromiumBased: false,
    isUnsupportedBrowser: false,
    unsupportedBrowserType: null,
    canRecord: false
  };
}
